import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useFreeze } from './freezeOwner.js';

/**
 * @file THE SHOP PANEL: the till's list of things you can buy, the money you
 * have, and one key to leave. Built on `Cutscene.jsx`'s mould — read that file's
 * header first; every trap it documents applies here too.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT IS A PURE RENDERER, AND THAT IS THE WHOLE DESIGN
 * ─────────────────────────────────────────────────────────────────────────────
 * This component MAY NOT decide anything about a shop. It does not compare
 * `price` to `cash`, does not decide what "affordable" means, does not format an
 * effect into prose, and does not know a single shop KIND or effect TYPE by
 * name. The engine has already made every one of those decisions and shipped
 * the answer in the row: **`state` is the only thing that decides whether a row
 * is enabled, and `reason` is the only thing that explains why it is not.**
 *
 * TWO PLACES COMPUTING AFFORDABILITY IS THE EXACT BUG THIS DESIGN EXISTS TO
 * PREVENT. The engine's rule and the panel's rule drift the moment either grows
 * a discount, a stock cap, a licence check or a per-kind rule — and the symptom
 * is a row that greys out for a player who can afford it, or a row that buys
 * nothing when clicked. So: one authority, one answer, one renderer. The panel
 * must therefore work, unchanged, for a shop kind and an effect type that do not
 * exist yet — which is also the test of whether a change here is legal.
 *
 * The corollary is rule two: **NEVER HOLD A LOCAL COPY OF CASH, HP OR ITEMS.**
 * There is exactly one piece of state, the last snapshot the engine sent. A
 * purchase changes the screen because `'shop:state'` arrives, not because
 * anything here mutated. If the engine refuses a buy, the screen simply does not
 * change, which is the correct picture — no optimistic row, nothing to roll back.
 *
 * The one thing printed that is not `title`/`label`/`effect`/`reason` is `kind`,
 * and it is printed VERBATIM as a subtitle — a passthrough, never a lookup. A
 * kind this file has never heard of prints itself and nothing breaks.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * engine → ui   'shop:open'   { token, kind, title, cash, items: [Row] }
 * engine → ui   'shop:state'  same payload, republished after every purchase
 * engine → ui   'shop:close'  { token }        ⚠ two-way, see below
 * ui → engine   'shop:buy'    { token, id }
 * ui → engine   'shop:close'  { token }
 *
 * Row = { id, label, price, effect, state, reason }
 * state ∈ 'buy' | 'poor' | 'full' | 'owned'      reason = string | null
 * ```
 *
 * ⚠ `'shop:close'` IS NOT ONLY OURS TO SEND — this is `Cutscene.jsx`'s
 * `'mission:cutscene:end'` lesson, and it is the unrecoverable one. The engine
 * closes the shop from its own side when the level is torn down, when the player
 * dies at the till, or when the room goes away; a panel left on screen over a
 * frozen loop with nothing left to answer it is a dead game. So this listens to
 * the channel it writes to, filtered by `sentRef` so it never hears its own emit.
 *
 * ⚠ AND THE CLOSE IS EMITTED EXACTLY ONCE PER TOKEN. `sentRef` latches it, so a
 * double click on Leave, or Esc landing in the same frame as a click, cannot
 * hand the engine two answers for one visit.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT FREEZES THE SIM, AND IT EATS THE KEYBOARD
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine.loop.setTimeScale(0)` while open — never `loop.stop()`; see
 * `PauseMenu.jsx`'s header. The effect's CLEANUP ALWAYS restores 1, so an
 * unmount (route change, engine teardown, an engine-side close) can never leave
 * a live engine frozen at zero. Held keys are dropped on BOTH edges via
 * `clearGameInput`, or a key held when the panel opened is still held when it
 * closes and the player walks out of the shop on their own.
 *
 * Keyboard is a CAPTURE listener on `window` with `stopImmediatePropagation` —
 * plain `stopPropagation` does NOT keep a key out of `engine/input/Keyboard.js`,
 * which listens on the same node in the bubble phase. Only `keydown` is
 * swallowed, never `keyup` (swallowing keyup strands a held key in
 * `Input.downKeys` forever), and a modified chord (Ctrl/Meta/Alt) is swallowed
 * but never acted on, so Ctrl+R still reloads.
 *
 * ⚠⚠ AND THE LISTENER IS REGISTERED ONCE, AT MOUNT, IN A `useLayoutEffect` —
 * NOT when the panel opens. THIS IS REGISTRATION ORDER AND IT IS LOAD-BEARING.
 * Capture listeners on the SAME node fire in the order they were ADDED, and
 * `PauseMenu.jsx` adds its own Escape-swallowing window-capture listener when
 * the HUD mounts. A listener added later — i.e. one added when a shop opens —
 * NEVER SEES ESCAPE: the pause menu takes it, calls `stopImmediatePropagation`,
 * and opens PAUSED underneath this panel (z-40 under z-70, so the player cannot
 * even see what happened) — and then restores the time scale to 1 on its own
 * cleanup, leaving the world running behind an open shop. That is precisely the
 * failure `Cutscene.jsx`'s header describes, and it was REPRODUCED IN THE
 * BROWSER here, not reasoned about. React runs LAYOUT effects for a commit
 * before any PASSIVE effect of that commit, so registering in `useLayoutEffect`
 * at mount beats a `useEffect` in a component that mounts earlier in the tree.
 * ⚠ The deps are `[]` and MUST STAY `[]` — re-registering moves us to the BACK
 * of the queue and silently restores the bug — which is why the live handler is
 * rebuilt into `keyRef` every render instead of being closed over here, and why
 * the handler's FIRST line returns without touching the event when no shop is
 * open (this listener outlives the panel).
 * ⚠ It does NOT make the panel's Esc win over a pause menu that is ALREADY up:
 * the ordering fix is one-directional. In practice a shop cannot open while the
 * loop is frozen, so that state is not reachable from the game.
 *
 * The highlight opens on the first row that can actually be bought, and holds
 * the player's row across a republish — see `cursorFor`, which is the whole
 * rule and is derived from `state` alone.
 *
 * ⚠ ESCAPE IS SWALLOWED, NOT FORWARDED. A pause menu opened over this panel
 * would restore the time scale to 1 on its own cleanup and leave the world
 * running behind the shop — the same single-owner rule `Cutscene.jsx` records.
 * Esc leaves the shop instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAYOUT
 * ─────────────────────────────────────────────────────────────────────────────
 * `z-[70]`, above `Cutscene`'s 60: a till is the innermost thing on screen and
 * nothing may cover it — see the layout block in `routes/PlayRoute.jsx`. The
 * list scrolls INSIDE the panel (capped height) so twelve rows behave, and a
 * row's optional `group` string draws as a section header if one is ever present
 * — a future boutique has categories, and it gets them without a code change.
 * Tabs are deliberately not built: one scrolling list is legible at a glance and
 * a tab strip is a second navigation model for a screen the player is in for
 * four seconds.
 *
 * Returns `null` until a snapshot arrives, so a level with no shops is
 * byte-identical to a build without this file.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Keys that leave the shop. F is the key that opened it. */
const CLOSE_KEYS = new Set(['Escape', 'KeyF']);

/** Keys that buy the highlighted row. */
const BUY_KEYS = new Set(['Enter', 'NumpadEnter', 'Space']);

export default function ShopPanel({ engine }) {
  /**
   * THE ONLY STATE: the last snapshot the engine sent. Never a derived copy of
   * cash, never a locally patched item list.
   * @type {[null|{token: string, kind?: string, title?: string, cash?: any, items?: any[]}, Function]}
   */
  const [shop, setShop] = useState(null);
  /** Keyboard highlight. Presentation only — it decides nothing about a buy. */
  const [cursor, setCursor] = useState(0);
  /** Latches our close emit, so one token can only ever be answered once. */
  const sentRef = useRef('');
  const shopRef = useRef(null);
  const cursorRef = useRef(0);
  const listRef = useRef(null);
  /** The live keydown handler. Rebuilt every render, registered exactly once. */
  const keyRef = useRef(null);

  shopRef.current = shop;
  cursorRef.current = cursor;

  /**
   * Moves the highlight, keeping the ref coherent with the state. ⚠ The ref
   * matters as much as the state here: a buy emits into the engine, which
   * republishes `'shop:state'` SYNCHRONOUSLY inside that same call, and the
   * handler reads `cursorRef` long before React has re-rendered.
   */
  const place = useCallback((i) => {
    cursorRef.current = i;
    setCursor(i);
  }, []);

  /**
   * Takes a snapshot and puts the highlight where it belongs — see
   * `cursorFor`. Same coherence rule as `place`.
   * @param {any} snap
   * @param {any} keepId id under the cursor before this snapshot, or null.
   */
  const adopt = useCallback(
    (snap, keepId) => {
      shopRef.current = snap;
      setShop(snap);
      place(cursorFor(snap, keepId));
    },
    [place],
  );

  // ── subscribe ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    const offOpen = bus.on('shop:open', (p) => {
      if (!p?.token) return;
      // The engine is the authority on what is open; a fresh open re-arms the
      // latch, so a second visit to the same till can still be answered.
      sentRef.current = '';
      adopt(p, null);
    });

    // Republished after every purchase. Accepted ONLY for the token we are
    // showing: a state for a shop we are not in must not resurrect the panel,
    // and a state for a token we already closed must not either.
    const offState = bus.on('shop:state', (p) => {
      if (!p?.token) return;
      const cur = shopRef.current;
      if (!cur || cur.token !== p.token) return;
      // ⚠ HOLD THE PLAYER'S ROW ACROSS A REPUBLISH. Carry the id under the
      // cursor into the new snapshot rather than re-deriving from scratch, or
      // every purchase jumps the selection out from under a player mid-click.
      adopt(p, rowsOf(cur)[cursorRef.current]?.id ?? null);
    });

    // ⚠ The two-way half. Ours are filtered out by `sentRef`, so this can only
    // ever fire for somebody else's close — the level going away, the player
    // dying at the till, the room being disposed under us.
    const offClose = bus.on('shop:close', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!token || sentRef.current === token) return;
      if (shopRef.current?.token !== token) return;
      shopRef.current = null;
      setShop(null);
      place(0);
    });

    return () => {
      offOpen?.();
      offState?.();
      offClose?.();
    };
  }, [engine, adopt, place]);

  // A new engine means the old world is gone — never leave a disposed level's
  // shop on screen, and never answer its token afterwards.
  useEffect(() => {
    shopRef.current = null;
    setShop(null);
    setCursor(0);
    cursorRef.current = 0;
    sentRef.current = '';
  }, [engine]);

  // ── freeze the sim + clear held keys ───────────────────────────────────────
  // Keyed on the BOOLEAN, not on the snapshot: `'shop:state'` hands us a new
  // object after every purchase, and re-running this on each one would flick the
  // time scale 0 → 1 → 0 for no reason.
  // ⚠ AND IT GOES THROUGH `freezeOwner.js` NOW, not through `setTimeScale`. This
  // file's header calls itself "the innermost of the three overlays that freeze
  // the loop" and rests on being LAST; that ordering argument is dead now that a
  // phone can ring over a till. The claim is reference-counted, so this cleanup
  // restarts the world only when nothing else is still holding it frozen.
  const open = !!shop;
  useFreeze(engine, 'shop-panel', open);
  useEffect(() => {
    if (!open) return undefined;
    clearGameInput(engine);
    return () => clearGameInput(engine);
  }, [engine, open]);

  // ── actions ────────────────────────────────────────────────────────────────
  /** Leave. Answers the engine exactly once for this token. */
  const close = useCallback(() => {
    const s = shopRef.current;
    if (!s) return;
    if (sentRef.current !== s.token) {
      sentRef.current = s.token;
      engine?.bus?.emit?.('shop:close', { token: s.token });
    }
    shopRef.current = null;
    setShop(null);
    place(0);
  }, [engine, place]);

  /**
   * Buy row `i`. ⚠ The ONLY gate is `state === 'buy'` — no price arithmetic
   * lives here or ever may. Nothing is mutated: the screen changes when the
   * engine republishes `'shop:state'`.
   * @param {number} i
   */
  const buy = useCallback(
    (i) => {
      const s = shopRef.current;
      const row = rowsOf(s)[i];
      if (!s || !row || row.state !== 'buy' || row.id == null) return;
      // Before the emit: the engine answers with `'shop:state'` synchronously,
      // and that handler carries the row under the cursor into the new
      // snapshot — so the cursor must already be on the row being bought.
      place(i);
      engine?.bus?.emit?.('shop:buy', { token: s.token, id: row.id });
    },
    [engine, place],
  );

  /** @param {number} step */
  const move = useCallback(
    (step) => {
      const rows = rowsOf(shopRef.current);
      if (!rows.length) return;
      place((cursorRef.current + step + rows.length) % rows.length);
    },
    [place],
  );

  // ── keyboard ───────────────────────────────────────────────────────────────
  // ⚠ THE LIVE HANDLER LIVES IN A REF, AND THE LISTENER IS REGISTERED ONCE, AT
  // MOUNT, IN A LAYOUT EFFECT. See the header — this is registration ORDER, not
  // taste, and it is the difference between Esc closing the shop and Esc opening
  // the pause menu behind it. Re-registering would put us back at the end of the
  // queue, so this closure is rebuilt every render and the listener never is.
  keyRef.current = (e) => {
    // Closed: not our keyboard. Return WITHOUT touching the event — this
    // listener outlives the panel, and swallowing here would eat the whole
    // game's input.
    if (!shopRef.current) return;

    // A chord belongs to the browser: swallow it so the sim never sees it, but
    // never act on it and never cancel it (Ctrl+R must still reload).
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.stopImmediatePropagation();
      return;
    }

    if (CLOSE_KEYS.has(e.code)) {
      // ⚠ Esc is SWALLOWED, never forwarded — a pause menu over this panel
      // would restore the time scale on its own cleanup. See the header.
      e.preventDefault();
      e.stopImmediatePropagation();
      close();
      return;
    }

    // 1–9 buy the Nth row, and only if that row says `'buy'`.
    const digit = digitOf(e.code);
    if (digit > 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
      buy(digit - 1);
      return;
    }

    if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
      e.preventDefault();
      e.stopImmediatePropagation();
      move(e.code === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (e.code === 'Tab') {
      // Kept inside the panel: focus escaping to the page chrome mid-shop is
      // how a player ends up typing into nothing.
      e.preventDefault();
      e.stopImmediatePropagation();
      move(e.shiftKey ? -1 : 1);
      return;
    }

    if (BUY_KEYS.has(e.code)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      buy(cursorRef.current);
      return;
    }

    // Everything else is swallowed: WASD at the till must not survive into the
    // first step after the player walks back out.
    e.stopImmediatePropagation();
  };

  // ⚠ `useLayoutEffect`, deps `[]`, AND NEVER RE-RUN. Both halves are the fix.
  useLayoutEffect(() => {
    const onKeyDown = (e) => keyRef.current?.(e);
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Keep the highlighted row in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-shop-row="${cursor}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, cursor]);

  if (!shop) return null;

  const rows = rowsOf(shop);
  const at = rows.length ? Math.min(cursor, rows.length - 1) : -1;

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-[70] flex items-center justify-center bg-void/85 backdrop-blur-[2px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof shop.title === 'string' ? shop.title : 'Shop'}
        className="flex max-h-[86vh] w-[min(38rem,92vw)] flex-col rounded-[4px] border border-hairline bg-ink/95 shadow-[0_18px_60px_rgba(0,0,0,0.6)]"
      >
        {/* ── header: the shop's name, and the money, in the genre's colour ── */}
        <header className="flex items-start justify-between gap-6 border-b border-hairline px-5 py-4">
          <div className="min-w-0">
            <h2 className="gta-outline truncate font-display text-2xl uppercase leading-none text-hud">
              {typeof shop.title === 'string' && shop.title ? shop.title : 'Shop'}
            </h2>
            {/* VERBATIM, never mapped — an unknown kind prints itself. */}
            {typeof shop.kind === 'string' && shop.kind ? (
              <div className="mt-1.5 font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint">
                {shop.kind}
              </div>
            ) : null}
          </div>

          {/* Matches `CashBar.jsx`: gold, outlined, sign a step smaller. */}
          <div
            className="shrink-0 select-none rounded-md border border-hairline bg-void/70 px-2.5 py-1"
            role="status"
            aria-label={`Cash ${moneyText(shop.cash)}`}
          >
            <div className="flex items-baseline justify-end gap-[2px]">
              <span className="gta-outline font-display text-[15px] leading-none text-gold/80">$</span>
              <span className="gta-outline font-display text-[22px] leading-none text-gold tabular-nums">
                {moneyText(shop.cash)}
              </span>
            </div>
          </div>
        </header>

        {/* ── the stock ─────────────────────────────────────────────────────── */}
        <div ref={listRef} className="max-h-[54vh] min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {rows.length === 0 ? (
            <p className="px-2 py-6 text-center font-hud text-[13px] uppercase tracking-[0.2em] text-hud-faint">
              Nothing for sale
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {rows.map((row, i) => (
                <li key={rowKey(row, i)}>
                  {/* A group header appears only when the row carries one and it
                      differs from the row above — no grouping model, no tabs. */}
                  {groupHeader(rows, i) ? (
                    <div className="mb-1.5 mt-3 px-2 font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint first:mt-0">
                      {groupHeader(rows, i)}
                    </div>
                  ) : null}
                  <ShopRow
                    row={row}
                    index={i}
                    selected={i === at}
                    onBuy={() => buy(i)}
                    onHover={() => setCursor(i)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── footer ────────────────────────────────────────────────────────── */}
        <footer className="flex items-center justify-between gap-4 border-t border-hairline px-5 py-3">
          <span className="font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint">
            Esc / F — leave
          </span>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={close}
            className="rounded-[3px] border border-hairline bg-void/60 px-4 py-1.5 font-display text-sm uppercase tracking-wide text-hud transition hover:border-gold hover:text-gold"
          >
            Leave
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * One line of stock. ⚠ `state === 'buy'` is the ONLY thing that enables it, and
 * every other state shares ONE disabled look on purpose: telling 'poor' from
 * 'owned' by colour would be this file forming an opinion about why, and `reason`
 * is the thing that says why. The price is shown on a disabled row too — a
 * player has to be able to see what they are saving up for.
 *
 * @param {{row: any, index: number, selected: boolean, onBuy: Function, onHover: Function}} props
 */
function ShopRow({ row, index, selected, onBuy, onHover }) {
  const can = row.state === 'buy';
  const reason = typeof row.reason === 'string' && row.reason ? row.reason : '';
  const effect = typeof row.effect === 'string' && row.effect ? row.effect : '';

  return (
    <button
      type="button"
      data-shop-row={index}
      disabled={!can}
      aria-disabled={!can}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => onHover()}
      onClick={can ? () => onBuy() : undefined}
      className={`group flex w-full items-center gap-3 rounded-[3px] border px-3 py-2.5 text-left transition ${
        can
          ? 'cursor-pointer border-hairline bg-steel/50 hover:border-gold hover:bg-steel'
          : 'cursor-not-allowed border-hairline/50 bg-ink/60 opacity-60'
      } ${selected ? 'ring-1 ring-gold/70' : ''}`}
    >
      {/* The number key that buys it. Only the first nine rows have one. */}
      <span
        aria-hidden="true"
        className={`w-4 shrink-0 text-center font-hud text-[11px] font-bold tabular-nums ${
          can ? 'text-hud-faint group-hover:text-gold' : 'text-hud-faint/60'
        }`}
      >
        {index < 9 ? index + 1 : ''}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[15px] uppercase leading-none tracking-[0.04em] text-hud">
          {typeof row.label === 'string' ? row.label : String(row.id ?? '')}
        </span>
        {/* Printed VERBATIM. This file does not know what an effect means. */}
        {effect ? (
          <span className="mt-1 block truncate font-hud text-[12px] leading-none text-hud-dim">
            {effect}
          </span>
        ) : null}
        {!can && reason ? (
          <span className="mt-1 block truncate font-hud text-[11px] uppercase leading-none tracking-[0.16em] text-hud-faint">
            {reason}
          </span>
        ) : null}
      </span>

      <span className="shrink-0 self-start pt-[1px] font-display text-[15px] leading-none tabular-nums text-gold">
        {priceText(row.price)}
      </span>
    </button>
  );
}

/**
 * The rows of a snapshot, defensively. A malformed payload draws an empty shop
 * rather than throwing inside the HUD tree, which would take the whole overlay
 * (and the time-scale cleanup with it).
 * @param {any} shop
 * @returns {any[]}
 */
function rowsOf(shop) {
  return Array.isArray(shop?.items) ? shop.items : [];
}

/**
 * Where the highlight goes for a snapshot.
 *
 * ⚠ A HIGHLIGHT IS A PROMISE, the same way a prompt is (`docs/shops.md`: a
 * prompt for a key that refuses is worse than no prompt) — one layer in. The
 * panel used to open the cursor on row 0 unconditionally, so an all-`'full'`
 * diner opened with Enter and Space pointed at a row that does nothing.
 *
 * So: the FIRST row that says `'buy'`, falling back to row 0 when no row does —
 * a shop where nothing is buyable still has to highlight something, and there
 * the highlight is honestly inert.
 *
 * ⚠ Two constraints, and both are load-bearing:
 *  1. `keepId` WINS. On an ordinary republish after a purchase the player's row
 *     is still under their pointer, and re-deriving would jump the selection out
 *     from under a mid-click player (and would walk the cursor down the list on
 *     every buy as rows ahead of it turn `'owned'`). The row is followed by ID,
 *     so it survives being re-ordered; only a row that has LEFT the list gives
 *     the highlight back to this rule.
 *  2. It reads `state` and NOTHING ELSE. No price, no cash, no notion of what
 *     is affordable — that is the pure-renderer rule in this file's header, and
 *     "which row do we start on" is exactly the kind of small convenience that
 *     grows a second affordability model if it is allowed to look at a price.
 *
 * @param {any} shop
 * @param {any} keepId The id under the cursor before this snapshot, or null.
 * @returns {number}
 */
function cursorFor(shop, keepId) {
  const rows = rowsOf(shop);
  if (!rows.length) return 0;
  if (keepId != null) {
    const held = rows.findIndex((r) => r?.id === keepId);
    if (held >= 0) return held;
  }
  const buyable = rows.findIndex((r) => r?.state === 'buy');
  return buyable >= 0 ? buyable : 0;
}

/**
 * The section header for row `i`, or `''` — a row's `group` prints when it
 * differs from the row above it. No sorting, no bucketing: the engine's order is
 * the order.
 * @param {any[]} rows
 * @param {number} i
 * @returns {string}
 */
function groupHeader(rows, i) {
  const g = rows[i]?.group;
  if (typeof g !== 'string' || !g) return '';
  const prev = rows[i - 1]?.group;
  return prev === g ? '' : g;
}

/**
 * @param {any} row
 * @param {number} i
 * @returns {string}
 */
function rowKey(row, i) {
  return row?.id != null ? `${row.id}` : `row-${i}`;
}

/**
 * `1234` → `1,234`. Presentation of a number the engine already decided, never
 * arithmetic on it; a non-number is printed as it arrived.
 * @param {any} n
 * @returns {string}
 */
function moneyText(n) {
  if (typeof n === 'number' && Number.isFinite(n)) return Math.floor(n).toLocaleString('en-US');
  return n == null ? '0' : String(n);
}

/**
 * A price as the row gave it: `$1,234` for a number, verbatim for anything else
 * (a future "trade-in" or "free" is a string and must survive this file).
 * @param {any} p
 * @returns {string}
 */
function priceText(p) {
  if (typeof p === 'number' && Number.isFinite(p)) return `$${Math.floor(p).toLocaleString('en-US')}`;
  return typeof p === 'string' ? p : '';
}

/**
 * `'Digit3'`/`'Numpad3'` → 3, anything else → 0.
 * @param {string} code
 * @returns {number}
 */
function digitOf(code) {
  const m = /^(?:Digit|Numpad)([1-9])$/.exec(code);
  return m ? Number(m[1]) : 0;
}

/**
 * Drops any key the sim thinks is held. Reached through `engine.systems`, the
 * same way `Cutscene.jsx` and `PauseMenu.jsx` do it — `createGameplay` already
 * exposes its `Input`, so this needs no new engine surface.
 * @param {any} engine
 */
function clearGameInput(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return;
  for (const s of systems) {
    if (s?.name === 'gameplay') s.input?.clear?.();
  }
}
